import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import LazLogo from "@/components/LazLogo";

const IMPROVE_AREAS = [
  "Leadership", "Confidence", "Career Growth", "Emotional Regulation",
  "Accountability", "Personal Structure & Discipline",
];

const PACKAGES = [
  { id: "individual_standard", title: "Individual — Standard", price: "$160 CAD/session (or $150 on a 3-month term)" },
  { id: "individual_premium", title: "Individual — Premium", price: "$600 CAD/month · 4 sessions" },
  { id: "individual_executive", title: "Individual — Executive", price: "$1,500 CAD/month minimum" },
  { id: "group_coaching", title: "Group Coaching", price: "$75–$90 CAD/person/session" },
  { id: "corporate_coaching", title: "Corporate Coaching", price: "From $1,200 CAD/month" },
  { id: "speaking_appearance", title: "Speaking, MC & Appearances", price: "From $700 CAD" },
  { id: "not_sure", title: "Not sure yet", price: "Get guidance from Hufrey" },
];

const CONSENTS = [
  { key: "consent_not_therapy", label: "I understand LAZ Coach is not a licensed therapy, psychiatric, or financial advisory service." },
  { key: "consent_payments_final", label: "I understand all payments are final once processed for the billing cycle." },
  { key: "consent_subscription_nonrefundable", label: "I understand subscription packages are prepaid and non-refundable once the month begins." },
  { key: "consent_cancellation_policy", label: "I understand the cancellation and missed-session policy." },
  { key: "consent_discount_verification", label: "I understand discount eligibility requires verification." },
  { key: "consent_terms", label: "I agree to the LAZ Coach Terms & Client Agreement." },
];

const STEPS = ["Your info", "Your goals", "Your package", "Consent"];

export default function Apply() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", city_country: "", profession: "",
    improve_areas: [], improve_other: "", biggest_challenge: "", expected_outcome: "",
    package_interest: "", ylm_referral: false,
    consent_not_therapy: false, consent_payments_final: false,
    consent_subscription_nonrefundable: false, consent_cancellation_policy: false,
    consent_discount_verification: false, consent_terms: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArea = (a) =>
    setForm((f) => ({
      ...f,
      improve_areas: f.improve_areas.includes(a)
        ? f.improve_areas.filter((x) => x !== a)
        : [...f.improve_areas, a],
    }));

  const canNext = () => {
    if (step === 0) return form.name && form.email && form.password.length >= 6 && form.phone && form.city_country;
    if (step === 1) return form.biggest_challenge && form.expected_outcome;
    if (step === 2) return !!form.package_interest;
    return true;
  };

  const submit = async () => {
    setError("");
    const allConsented = CONSENTS.every((c) => form[c.key]);
    if (!allConsented) {
      setError("Please accept all consent items before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/applications", form);
      setUser(data.user);
      setSubmitted(true);
      toast.success("Application submitted!");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div data-testid="apply-success" className="max-w-md w-full text-center rounded-3xl border border-stone-200 bg-stone-50 p-10 shadow-sm">
          <CheckCircle2 size={48} strokeWidth={1.2} className="mx-auto text-green-700" />
          <h1 className="font-serif text-3xl text-stone-900 mt-5">Application received</h1>
          <p className="text-stone-600 mt-3 text-sm leading-relaxed">
            Thank you, {form.name.split(" ")[0]}. Hufrey will personally review your application
            and follow up shortly. You can track its status any time from your dashboard.
          </p>
          <Button
            data-testid="apply-go-dashboard"
            onClick={() => navigate("/dashboard", { replace: true })}
            className="mt-8 rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50 px-7 h-11"
          >
            Go to my dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <LazLogo size={44} className="mb-8" />
        <div className="mb-2">
          <Link to="/" className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1">
            <ArrowLeft size={13} strokeWidth={1.5} /> Back to home
          </Link>
        </div>
        <h1 className="font-serif text-4xl text-stone-900 tracking-tight">Client application</h1>
        <p className="text-stone-500 mt-2 text-sm">
          A short pre-screening form so Hufrey can understand your goals before your first session.
        </p>

        {/* Progress */}
        <div className="mt-8 flex gap-2" data-testid="apply-progress">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= step ? "bg-amber-700" : "bg-stone-200"}`} />
          ))}
        </div>
        <div className="mt-2 text-xs uppercase tracking-[0.18em] text-amber-700">{STEPS[step]}</div>

        <div className="mt-6 rounded-3xl bg-white border border-stone-200 p-6 sm:p-8 space-y-5">
          {step === 0 && (
            <>
              <div>
                <Label className="text-stone-700">Full name</Label>
                <Input data-testid="apply-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2 rounded-xl border-stone-200 h-11" placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="text-stone-700">Email</Label>
                <Input data-testid="apply-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 rounded-xl border-stone-200 h-11" placeholder="you@example.com" />
              </div>
              <div>
                <Label className="text-stone-700">Password</Label>
                <Input data-testid="apply-password-input" type="password" minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-2 rounded-xl border-stone-200 h-11" placeholder="At least 6 characters" />
              </div>
              <div>
                <Label className="text-stone-700">Phone number</Label>
                <Input data-testid="apply-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 rounded-xl border-stone-200 h-11" placeholder="204-555-0100" />
              </div>
              <div>
                <Label className="text-stone-700">City & country</Label>
                <Input data-testid="apply-city-input" value={form.city_country} onChange={(e) => set("city_country", e.target.value)} className="mt-2 rounded-xl border-stone-200 h-11" placeholder="Winnipeg, Canada" />
              </div>
              <div>
                <Label className="text-stone-700">Current profession / role (optional)</Label>
                <Input data-testid="apply-profession-input" value={form.profession} onChange={(e) => set("profession", e.target.value)} className="mt-2 rounded-xl border-stone-200 h-11" placeholder="Operations Manager" />
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div>
                <Label className="text-stone-700">What area would you like to improve?</Label>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {IMPROVE_AREAS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      data-testid={`apply-area-${a.replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => toggleArea(a)}
                      className={`text-left px-4 py-2.5 rounded-xl border text-sm transition-all ${
                        form.improve_areas.includes(a) ? "border-amber-700 bg-amber-700/5 text-stone-900" : "border-stone-200 text-stone-600 hover:bg-stone-50"
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
                <Input
                  data-testid="apply-area-other-input"
                  value={form.improve_other}
                  onChange={(e) => set("improve_other", e.target.value)}
                  className="mt-3 rounded-xl border-stone-200 h-11"
                  placeholder="Other (explain)..."
                />
              </div>
              <div>
                <Label className="text-stone-700">What is your biggest current challenge?</Label>
                <Textarea data-testid="apply-challenge-input" rows={4} value={form.biggest_challenge} onChange={(e) => set("biggest_challenge", e.target.value)} className="mt-2 rounded-2xl border-stone-200" />
              </div>
              <div>
                <Label className="text-stone-700">What result are you expecting from coaching in the next 3–6 months?</Label>
                <Textarea data-testid="apply-outcome-input" rows={4} value={form.expected_outcome} onChange={(e) => set("expected_outcome", e.target.value)} className="mt-2 rounded-2xl border-stone-200" />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Label className="text-stone-700">Which package interests you?</Label>
              <div className="grid gap-2.5">
                {PACKAGES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`apply-package-${p.id}`}
                    onClick={() => set("package_interest", p.id)}
                    className={`text-left px-4 py-3.5 rounded-2xl border transition-all ${
                      form.package_interest === p.id ? "border-amber-700 bg-amber-700/5" : "border-stone-200 hover:bg-stone-50"
                    }`}
                  >
                    <div className="font-medium text-stone-900 text-sm">{p.title}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{p.price}</div>
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-3 mt-4 text-sm text-stone-600 cursor-pointer">
                <Checkbox data-testid="apply-ylm-checkbox" checked={form.ylm_referral} onCheckedChange={(v) => set("ylm_referral", !!v)} />
                I'm a verified Youth Leadership Movement (YLM) member or referral (25% partnership discount, subject to verification).
              </label>
            </>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <p className="text-sm text-stone-500 mb-2">Please confirm you've read and agree to the following:</p>
              {CONSENTS.map((c) => (
                <label key={c.key} className="flex items-start gap-3 text-sm text-stone-700 cursor-pointer">
                  <Checkbox data-testid={`apply-${c.key}`} checked={form[c.key]} onCheckedChange={(v) => set(c.key, !!v)} className="mt-0.5" />
                  {c.label}
                </label>
              ))}
              <div className="text-xs text-stone-400 pt-2">
                Read the full{" "}
                <Link to="/legal/terms" target="_blank" className="text-amber-700 hover:underline">Terms & Client Agreement</Link>.
              </div>
            </div>
          )}

          {error && (
            <div data-testid="apply-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-xl">
              {error}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button
              type="button"
              variant="ghost"
              data-testid="apply-back-button"
              disabled={step === 0}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="rounded-full text-stone-600 disabled:opacity-0"
            >
              <ArrowLeft size={14} strokeWidth={1.5} className="mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                data-testid="apply-next-button"
                disabled={!canNext()}
                onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50 disabled:opacity-50"
              >
                Next <ArrowRight size={14} strokeWidth={1.5} className="ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                data-testid="apply-submit-button"
                disabled={submitting}
                onClick={submit}
                className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50"
              >
                {submitting ? "Submitting..." : "Submit application"}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-stone-500">
          Already have an account?{" "}
          <Link to="/login" data-testid="apply-login-link" className="text-amber-700 hover:underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
